import { NavLink, useNavigate } from 'react-router-dom';
import { Camera, LayoutDashboard, LifeBuoy, LogOut, Megaphone, Package, Phone, Send, Settings2, Star, UserX, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../auth';
import { cn } from '../../lib/utils';
import { Logo } from '../Logo';

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const NAV: Item[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/jobs', label: 'Drops', icon: Package },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/stuck-signups', label: 'Stuck signups', icon: UserX },
  { to: '/ratings', label: 'Ratings', icon: Star },
  { to: '/support', label: 'Support', icon: LifeBuoy },
  { to: '/announcements', label: 'Announcements', icon: Megaphone },
  { to: '/outreach', label: 'Outreach', icon: Send },
  { to: '/ig-posts', label: 'IG Marketing', icon: Camera },
  { to: '/sender-accounts', label: 'Senders', icon: Phone },
  { to: '/llm-config', label: 'LLM config', icon: Settings2 },
];

export function Sidebar() {
  const { logout } = useAuth();
  const nav = useNavigate();

  async function handleLogout() {
    await logout();
    nav('/login');
  }

  return (
    <aside className="hidden border-r border-border bg-surface md:flex md:w-60 md:shrink-0 md:flex-col">
      <div className="flex h-14 items-center gap-2 px-5">
        <Logo width={108} />
        <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-brand-700 dark:bg-brand-900/40 dark:text-brand-500">
          Admin
        </span>
      </div>
      <nav className="flex flex-col gap-0.5 p-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-500'
                  : 'text-text-subtle hover:bg-surface-muted hover:text-text',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto p-2">
        <button
          type="button"
          onClick={handleLogout}
          className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-text-subtle transition-colors hover:bg-surface-muted hover:text-text"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
        <p className="px-3 pt-2 text-[10px] text-text-muted">
          v0.1.0-mvp · {new Date().getFullYear()}
        </p>
      </div>
    </aside>
  );
}

// Mobile bottom-nav fallback so the dashboard is at least navigable on a phone
// even though desktop is the primary surface (admin/ is full-width per CLAUDE
// invariant 9, no 480px lock).
export function MobileNav() {
  return (
    <nav className="flex h-14 items-center justify-around border-t border-border bg-surface md:hidden">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[10px] font-medium uppercase tracking-wider',
              isActive ? 'text-brand-700 dark:text-brand-500' : 'text-text-muted',
            )
          }
        >
          <item.icon className="h-5 w-5" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}
